import { useState } from 'react';
import type { FC, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { experimentalStyled } from '@material-ui/core/styles';
import { Box, Typography, Button } from '@material-ui/core';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import useAuth from '../../hooks/useAuth';

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayoutRoot = experimentalStyled('div')(
  ({ theme }) => (
    {
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
      width: '100%'
    }
  )
);

const DashboardLayoutWrapper = experimentalStyled('div')(
  ({ theme }) => (
    {
      display: 'flex',
      flex: '1 1 auto',
      overflow: 'hidden',
      paddingTop: '64px',
      [theme.breakpoints.up('lg')]: {
        paddingLeft: '280px'
      }
    }
  )
);

const DashboardLayoutContainer = experimentalStyled('div')({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden'
});

const DashboardLayoutContent = experimentalStyled('div')({
  flex: '1 1 auto',
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  WebkitOverflowScrolling: 'touch'
});

const DashboardLayout: FC<DashboardLayoutProps> = () => {
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState<boolean>(false);
  const { user, stopImpersonating } = useAuth();

  const bannerHeight = user?.isImpersonating ? 50 : 0;

  return (
    <DashboardLayoutRoot>
      {user?.isImpersonating && (
        <Box
          sx={{
            backgroundColor: 'error.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: bannerHeight,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2000,
            width: '100%',
            px: 2
          }}
        >
          <Typography variant="h6" sx={{ mr: 3, fontWeight: 'bold', fontSize: '1rem' }}>
            VIEWING AS: {user.name} ({user.email})
          </Typography>
          <Button
            color="inherit"
            variant="outlined"
            size="small"
            onClick={stopImpersonating}
            sx={{ 
                borderColor: 'white',
                '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderColor: 'white'
                }
            }}
          >
            Exit Impersonation
          </Button>
        </Box>
      )}
      <DashboardNavbar
        onSidebarMobileOpen={(): void => setIsSidebarMobileOpen(true)}
        sx={{ top: bannerHeight }}
      />
      <DashboardSidebar
        onMobileClose={(): void => setIsSidebarMobileOpen(false)}
        openMobile={isSidebarMobileOpen}
        sx={{ top: bannerHeight }}
      />
      <DashboardLayoutWrapper sx={{ pt: `${64 + bannerHeight}px` }}>
        <DashboardLayoutContainer>
          <DashboardLayoutContent sx={{ height: `calc(100% - ${bannerHeight}px)` }}>
            <Outlet />
          </DashboardLayoutContent>
        </DashboardLayoutContainer>
      </DashboardLayoutWrapper>
    </DashboardLayoutRoot>
  );
};

export default DashboardLayout;
